import { ErrorMessage } from '@atlaskit/form';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import { Radio } from '@atlaskit/radio';
import Toggle from '@atlaskit/toggle';
import { useCallback, useEffect, useState } from 'react';
import { type MessageDescriptor, useIntl } from 'react-intl-next';

import type { RepositorySettingValue } from '../../../../../../../typings/globals';
import { useRovoDevWebAnalyticsClient } from '../../../../../controllers/rovo-dev-web-analytics-events';
import authRequest from '../../../../../utils/fetch';
import { SET_CODE_REVIEWER_SETTING_URL } from '../../hooks/url';
import { parseStringBoolUndefined } from '../../utils';
import { CodeReviewerSettingTooltip } from '../code-reviewer-setting-tooltip';
import messages from '../messages';

const boxStyles = xcss({
	borderRadius: 'border.radius.075',
	backgroundColor: 'elevation.surface.sunken',
	padding: 'space.150',
});

type CodeReviewerAcceptanceCriteriaToggleProps = {
	repository: GH.Repository;
	updateRepoSettings: (update: RepositorySettingValue) => void;
};
export const CodeReviewerAcceptanceCriteriaToggle = ({
	repository,
	updateRepoSettings,
}: CodeReviewerAcceptanceCriteriaToggleProps) => {
	const { formatMessage } = useIntl();
	const codeReviewerEnabled = parseStringBoolUndefined(
		repository.autoreviewSettings?.value.autoreview_enabled,
	);
	const isACEnabled = parseStringBoolUndefined(
		repository.autoreviewSettings?.value.autoreview_acceptance_criteria_enabled,
	);
	const isACCommitCheckEnabled = parseStringBoolUndefined(
		repository.autoreviewSettings?.value.autoreview_acceptance_criteria_commit_trigger_enabled,
	);
	const [acceptanceCriteriaEnabled, setAcceptanceCriteriaEnabled] = useState<boolean>(isACEnabled);
	const [ACCommitCheckEnabled, setACCommitCheckEnabled] = useState<boolean>(isACCommitCheckEnabled);
	const [ACError, setACError] = useState<MessageDescriptor | null>(null);
	const [commitCheckError, setCommitCheckError] = useState<MessageDescriptor | null>(null);
	const { fireUiEvent } = useRovoDevWebAnalyticsClient();

	useEffect(() => {
		updateRepoSettings({
			autoreview_acceptance_criteria_enabled: acceptanceCriteriaEnabled,
			autoreview_acceptance_criteria_commit_trigger_enabled: ACCommitCheckEnabled,
			autoreview_enabled: codeReviewerEnabled,
		});
	}, [acceptanceCriteriaEnabled, ACCommitCheckEnabled]);

	const getAuthRequest = useCallback(
		(
			updatedValue:
				| { autoreview_acceptance_criteria_enabled: boolean }
				| { autoreview_acceptance_criteria_commit_trigger_enabled: boolean },
		) => {
			return authRequest(SET_CODE_REVIEWER_SETTING_URL, {
				method: 'post',
				body: JSON.stringify({
					settings: [
						{
							containerType: 'repository',
							containerId: repository.repoUrl,
							key: 'autoreview_settings',
							value: {
								autoreview_enabled: codeReviewerEnabled,
								autoreview_acceptance_criteria_enabled: isACEnabled,
								autoreview_acceptance_criteria_commit_trigger_enabled: isACCommitCheckEnabled,
								...updatedValue,
							},
						},
					],
				}),
				credentials: 'include',
				headers: {
					'content-type': 'application/json',
				},
			});
		},
		[],
	);

	const handleToggleACSetting = useCallback(async (newValue: boolean) => {
		fireUiEvent({
			action: 'clicked',
			actionSubject: `code-reviewer-acceptance-criteria-toggle-${newValue ? 'on' : 'off'}`,
			source: 'code-reviewer-repository-settings',
		});

		setACError(null);
		setAcceptanceCriteriaEnabled(newValue);

		try {
			const res = await fetch(getAuthRequest({ autoreview_acceptance_criteria_enabled: newValue }));

			if (!res.ok) {
				throw new Error(`http error status ${res.status} with text: ${res.statusText}`);
			}
		} catch (e) {
			setACError(messages.errorUpdateACSettings);
			setAcceptanceCriteriaEnabled(!newValue);
		}
	}, []);

	const handleToggleACCommitCheck = useCallback(async (newValue: boolean) => {
		fireUiEvent({
			action: 'clicked',
			actionSubject: `code-reviewer-acceptance-criteria-commit-check-radio-${newValue ? 'on' : 'off'}`,
			source: 'code-reviewer-repository-settings',
		});

		setCommitCheckError(null);
		setACCommitCheckEnabled(newValue);

		try {
			const res = await fetch(
				getAuthRequest({ autoreview_acceptance_criteria_commit_trigger_enabled: newValue }),
			);

			if (!res.ok) {
				throw new Error(`http error status ${res.status} with text: ${res.statusText}`);
			}
		} catch (e) {
			setCommitCheckError(messages.errorUpdateCommitCheck);
			setACCommitCheckEnabled(!newValue);
		}
	}, []);

	const disableCommitCheckRadios = !acceptanceCriteriaEnabled || !codeReviewerEnabled;

	return (
		<Stack space="space.100">
			<Stack space="space.100">
				<Text weight="bold">{formatMessage(messages.acceptanceCriteriaTitle)}</Text>
				<Flex alignItems="center" gap="space.050">
					<Toggle
						testId="settings-page-toggle"
						size="large"
						label=""
						onChange={() => handleToggleACSetting(!acceptanceCriteriaEnabled)}
						isChecked={acceptanceCriteriaEnabled && codeReviewerEnabled}
						isDisabled={!codeReviewerEnabled}
					/>
					<Text>{formatMessage(messages.acceptanceCriteriaToggleLabel)}</Text>
					<CodeReviewerSettingTooltip
						content={formatMessage(messages.acceptanceCriteriaTooltip)}
						label={formatMessage(messages.acceptanceCriteriaTooltipLabel)}
					/>
				</Flex>

				{ACError && (
					<ErrorMessage testId="code-reviewer-repository-ac-settings-error">
						{formatMessage(ACError)}
					</ErrorMessage>
				)}
			</Stack>

			<Box xcss={boxStyles}>
				<Stack space="space.100">
					<Text weight="bold">{formatMessage(messages.acceptanceCriteriaTimingTitle)}</Text>
					<Box>
						<Radio
							value="Check after every new commit"
							label={formatMessage(messages.acceptanceCriteriaCheckEveryCommit)}
							name="acceptance-criteria-check-after-every-commit"
							testId="settings-page-acceptance-criteria-check-after-every-commit"
							isDisabled={disableCommitCheckRadios}
							isChecked={isACCommitCheckEnabled && !disableCommitCheckRadios}
							onChange={() => handleToggleACCommitCheck(true)}
						/>
						<Radio
							value="Check only when pull request is created"
							label={formatMessage(messages.acceptanceCriteriaCheckOnPRCreation)}
							name="acceptance-criteria-check-only-on-pr-creation"
							testId="settings-page-acceptance-criteria-check-only-on-pr-creation"
							isDisabled={!acceptanceCriteriaEnabled || !codeReviewerEnabled}
							isChecked={!isACCommitCheckEnabled && !disableCommitCheckRadios}
							onChange={() => handleToggleACCommitCheck(false)}
						/>
					</Box>

					{commitCheckError && (
						<ErrorMessage testId="code-reviewer-repository-commit-check-settings-error">
							{formatMessage(commitCheckError)}
						</ErrorMessage>
					)}
				</Stack>
			</Box>
		</Stack>
	);
};
